import { useCallback, useEffect, useMemo, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { toast } from '@/components/ui/Toast'
import { brl } from '@/lib/format'
import { cn } from '@/lib/cn'
import { PartnerService } from '@/services/modules/PartnerService'
import type { CommissionsReport, CommissionTransaction, CommissionDetail } from '@/services/modules/RevenuesService'
import { currentMonthRange, ReportFilters } from '@/pages/Revenues/reportShared'
import { useAuthStore } from '@/stores/authStore'
import { CommissionDetailDrawer } from '@/components/CommissionDetailDrawer'

const ROLE_LABELS: Record<string, string> = { DIRETOR: 'Diretores', SUPERVISOR: 'Supervisores', AFILIADO: 'Afiliados', VENDEDOR: 'Vendedores' }
// Cargos que cada parceiro paga (abaixo dele na hierarquia).
const DOWNSTREAM: Record<string, string[]> = {
  DIRETOR:    ['SUPERVISOR', 'AFILIADO', 'VENDEDOR'],
  SUPERVISOR: ['AFILIADO', 'VENDEDOR'],
  AFILIADO:   ['VENDEDOR'],
}

export function PartnerReportCommissionsPage() {
  const init = currentMonthRange()
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [applied, setApplied] = useState({ from: init.from, to: init.to })

  const [summary, setSummary] = useState<CommissionsReport | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(false)

  const userType = useAuthStore(s => s.user?.type) ?? ''
  const roleFilters = useMemo(
    () => [{ value: '', label: 'Todos' }, ...(DOWNSTREAM[userType] ?? []).map(t => ({ value: t, label: ROLE_LABELS[t] }))],
    [userType],
  )

  const [type, setType] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [rows, setRows] = useState<CommissionTransaction[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loadingTx, setLoadingTx] = useState(false)

  const loadSummary = useCallback(async (f: string, t: string) => {
    setLoadingSummary(true)
    try { setSummary(await PartnerService.reportsCommissions(f, t)) }
    catch { toast.error('Erro ao carregar o resumo.') }
    finally { setLoadingSummary(false) }
  }, [])
  const loadTx = useCallback(async (f: string, t: string, ty: string, p: number, s: number) => {
    setLoadingTx(true)
    try {
      const res = await PartnerService.reportsCommissionTransactions({ from: f, to: t, type: ty || undefined, page: p, size: s })
      setRows(res.data); setMeta(res.meta)
    } catch { toast.error('Erro ao carregar as transações.') }
    finally { setLoadingTx(false) }
  }, [])

  useEffect(() => { loadSummary(applied.from, applied.to) }, [loadSummary, applied])
  useEffect(() => { loadTx(applied.from, applied.to, type, page, size) }, [loadTx, applied, type, page, size])

  function apply() { setApplied({ from, to }); setPage(1) }

  // Drawer de detalhamento (clique em Pendente/Consolidado).
  const [drawer, setDrawer] = useState<{ row: CommissionTransaction; status: 'consolidado' | 'pendente' } | null>(null)
  const [detail, setDetail] = useState<CommissionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const openDetail = useCallback(async (row: CommissionTransaction, status: 'consolidado' | 'pendente') => {
    setDrawer({ row, status })
    setDetail(null)
    setDetailLoading(true)
    try { setDetail(await PartnerService.reportsCommissionDetail({ from: applied.from, to: applied.to, memberId: row.userId, type: row.type, status })) }
    catch { toast.error('Erro ao carregar o detalhamento.') }
    finally { setDetailLoading(false) }
  }, [applied])

  const valueBtn = (active: boolean, cls: string) => cn('cursor-pointer underline-offset-2 hover:underline disabled:cursor-default disabled:no-underline', active ? cls : 'text-gray-300')

  const columns: DataTableColumn<CommissionTransaction>[] = [
    { key: 'user', header: 'Membro da rede', alwaysVisible: true, render: r => <span className="font-medium text-gray-800">{r.userName}</span> },
    { key: 'type', header: 'Papel', align: 'center', className: 'w-32', render: r => ROLE_LABELS[r.type] ?? r.type },
    { key: 'count', header: 'Incidências', align: 'center', className: 'w-28', render: r => <span className="text-gray-500">{r.count}</span> },
    { key: 'pendente', header: 'Pendente', align: 'right', className: 'w-32', render: r => (
      <button type="button" disabled={r.pendente <= 0} onClick={() => openDetail(r, 'pendente')} className={valueBtn(r.pendente > 0, 'font-medium text-amber-600')}>{brl(r.pendente)}</button>
    ) },
    { key: 'consolidado', header: 'Consolidado', align: 'right', className: 'w-32', render: r => (
      <button type="button" disabled={r.consolidado <= 0} onClick={() => openDetail(r, 'consolidado')} className={valueBtn(r.consolidado > 0, 'font-medium text-green-600')}>{brl(r.consolidado)}</button>
    ) },
    { key: 'total', header: 'Total', align: 'right', className: 'w-32', render: r => <span className="font-semibold text-gray-800">{brl(r.total)}</span> },
  ]

  return (
    <>
      <PageHeader title="Comissões" subtitle="O que você paga à sua rede direta, no período"
        crumbs={[{ label: 'Financeiro' }, { label: 'Relatórios' }, { label: 'Comissões' }]} />
      <ReportFilters from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={apply} loading={loadingSummary || loadingTx} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
        {(summary?.byType ?? []).map(c => (
          <div key={c.type} className="rounded-md bg-white ring-1 ring-gray-200 p-5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">{ROLE_LABELS[c.type] ?? c.type}</div>
            <div className="text-2xl font-semibold text-gray-800 mt-1">{brl(c.total)}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded bg-amber-50 px-2 py-1.5"><div className="text-amber-700 font-semibold">{brl(c.pendente)}</div><div className="text-gray-500">Pendente</div></div>
              <div className="rounded bg-green-50 px-2 py-1.5"><div className="text-green-700 font-semibold">{brl(c.consolidado)}</div><div className="text-gray-500">Consolidado</div></div>
            </div>
          </div>
        ))}
        {summary && summary.byType.length === 0 && (
          <div className="sm:col-span-3 text-sm text-gray-400 py-6 text-center">Nenhuma comissão a pagar no período.</div>
        )}
      </div>

      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700">Por comissionado</h3>
        <div className="inline-flex rounded-md ring-1 ring-gray-200 bg-white overflow-hidden">
          {roleFilters.map((opt, i) => (
            <button key={opt.value || 'all'} type="button" onClick={() => { setType(opt.value); setPage(1) }}
              className={cn('cursor-pointer px-3 py-1.5 text-[13px] font-medium transition min-w-[7.5rem] text-center', i > 0 && 'border-l border-gray-200',
                type === opt.value ? 'bg-brand-500 text-white hover:bg-brand-600' : 'text-gray-600 hover:bg-gray-50')}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <DataTable tableId="partner.commission-transactions" columns={columns} rows={rows} loading={loadingTx} getRowKey={r => r.id}
        emptyMessage="Nenhum comissionamento no período." />
      <Pagination meta={meta} size={size} onChange={setPage} onSizeChange={setSize} />

      <CommissionDetailDrawer
        open={drawer !== null}
        onClose={() => setDrawer(null)}
        title={drawer ? `${drawer.row.userName} — ${drawer.status === 'pendente' ? 'Pendente' : 'Consolidado'}` : 'Detalhamento'}
        subtitle="O que você paga a este membro, fatura a fatura"
        loading={detailLoading}
        detail={detail}
      />
    </>
  )
}

export default PartnerReportCommissionsPage
